import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import { DataGridPremium, GridToolbar } from '@mui/x-data-grid-premium';
import { SearchContext } from '../../context/SearchContext';
import { ThemeModeContext } from '../../App';


const SearchResults = (props) => {
    const { handleOpen } = props;
    const { setCaseId, searchResults, loadingSearch, noResults, getQuickViewPerCase } = useContext(SearchContext);
    const navigate = useNavigate();
    const { mode } = useContext(ThemeModeContext);
    const [loading, setLoading] = useState(Boolean);

    async function quickViewCase(e, params) {
        e.stopPropagation();
        setLoading(true);
        try {
            await getQuickViewPerCase(params.row.id);
            setLoading(false);
            setCaseId(params.row.id);
        } catch (err) {
            setLoading(false);
            console.log('error retrieving quick view results', err);
        }
    }

    const selectCase = (params) => { return (typeof params?.id === "string" && params?.id.startsWith('auto-generated')) ? null : navigate(`/view/${params.row.id}`); };

    const columns = [
        {
            id: 'caseStatus',
            field: 'caseStatus',
            headerName: 'Case Status',
            flex: 1,
            align: 'left',
            headerAlign: 'left',
        },
        {
            id: 'fullCaseNumber',
            field: 'fullCaseNumber',
            headerName: 'Case #',
            flex: 1,
            align: 'left',
            headerAlign: 'left',
        },
        {
            id: 'arrestNumber',
            field: 'arrestNumber',
            headerName: "Arrest #",
            flex: 1,
            align: 'left',
            headerAlign: 'left',
        },
        {
            id: 'citationNumber',
            field: 'citationNumber',
            headerName: "Citation #",
            flex: 1,
            align: 'left',
            headerAlign: 'left',
        },
        {
            id: 'clerkCaseNumber',
            field: 'clerkCaseNumber',
            headerName: 'Docket #',
            flex: 1,
            align: 'left',
            headerAlign: 'left',
        },
        {
            id: 'lastName',
            field: 'lastName',
            headerName: 'Last Name',
            flex: 1,
            align: 'left',
            headerAlign: 'left',
        },
        {
            id: 'firstName',
            field: 'firstName',
            headerName: 'First Name',
            flex: 1,
            align: 'left',
            headerAlign: 'left',
        },
        {
            id: 'description',
            field: 'description',
            headerName: 'Violations',
            flex: 1.5,
            align: 'left',
            headerAlign: 'left',
            renderCell: (params) => {
                return (
                    <Box>
                        {params.value?.split(",")?.map((x, index) => {
                            return <Typography key={index} variant="body2">{index + 1}. {x} </Typography>
                        })}
                    </Box>
                )
            }
        },
        {
            id: 'dateOfBirth',
            field: 'dateOfBirth',
            headerName: 'D.O.B',
            flex: 1,
            align: 'left',
            headerAlign: 'left',
        },
        {
            id: 'driverLicenseNumber',
            field: 'driverLicenseNumber',
            headerName: 'License #',
            flex: 1,
            align: 'left',
            headerAlign: 'left',
        },
        {
            id: 'violationDate',
            field: 'violationDate',
            headerName: 'Violation Date',
            flex: 1,
            align: 'left',
            headerAlign: 'left',
        },
        {
            id: 'courtDatetime',
            field: 'courtDatetime',
            headerName: 'Court Date',
            flex: 1,
            align: 'left',
            headerAlign: 'left',
        },
        {
            id: 'isDataMisMatch',
            field: 'isDataMisMatch',
            headerName: 'Data Mismatch',
            flex: 1,
            align: 'left',
            headerAlign: 'left',
            renderCell: (params) => {
                const isExpandableRow = params.rowNode?.type === "group";
                return (
                    <>
                        {(isExpandableRow && params.field === "__row_group_by_columns_group__") ? Boolean(params.value).toString() : (isExpandableRow && params.field !== "__row_group_by_columns_group__") ? null : Boolean(params.value).toString()}
                    </>
                )
            },
            valueFormatter: (params) => Boolean(params).toString(),
        },
        {
            id: 'quickView',
            field: 'quickView',
            headerName: 'Quick View Case',
            flex: 1,
            align: 'center',
            headerAlign: 'left',
            renderCell: (params) => {
                return (
                    <Button
                        onClick={async (e) => {
                            await quickViewCase(e, params);
                            handleOpen('quickCaseView');
                        }}
                        variant="contained"
                        sx={{ backgroundColor: "steelblue", color: 'white' }}
                    >
                        Quick View
                    </Button>
                )
            }
        },
    ];

    const cols = columns.map((col, index) => ({
        ...col,
        headerClassName: `column-header-${index}`,
        cellClassName: `column-cell-${index}`,
    }));

    function StyledGridOverlay(props) {
        const { children } = props;
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', backgroundColor: mode === 'dark' ? 'rgba(18, 18, 18, 0.9)' : 'rgba(255, 255, 255, 0.9)', }}>{children}</Box>
        )
    };

    function CircularProgressWithLabel(props) {
        return (
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <CircularProgress variant="indeterminate" {...props} />
            </Box>
        );
    };

    function CustomLoadingOverlay() {
        return (
            <StyledGridOverlay>
                <CircularProgressWithLabel />
                <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" component="div" color="text.primary">
                        One Moment While We Retrieve your Results...
                    </Typography>
                </Box>
            </StyledGridOverlay>
        );
    };

    if (loadingSearch && !noResults) {
        return (
            <Box>
                <Box sx={{ width: "5%", margin: "15vh auto" }}>
                    <CircularProgress />
                </Box>
            </Box>
        )
    };

    return (
        <Box>
            {
                noResults ? <h2 style={{ width: '99%', margin: '10vh auto', textAlign: 'center' }}>No Results</h2>
                    :
                    <Paper sx={{ width: "100%", maxWidth: "100%", mt: '1.5vh', height: "62.5vh", overFlow: "auto" }} elevation={10}>
                        <DataGridPremium
                            rows={searchResults}
                            columns={cols}
                            pagination
                            slots={{
                                toolbar: GridToolbar,
                                loadingOverlay: CustomLoadingOverlay
                            }}
                            loading={loading}
                            slotProps={{
                                toolbar: {
                                    showQuickFilter: true,
                                    quickFilterProps: { debounceMs: 500 },
                                    printOptions: { disableToolbarButton: true },
                                    csvOptions: { disableToolbarButton: true },
                                    excelOptions: { disableToolbarButton: true },
                                },
                            }}
                            disableMultipleRowSelection
                            onRowClick={selectCase}
                            getRowHeight={() => 'auto'}
                            //density="compact"
                            //autoHeight
                            disableColumnResize
                            sx={{
                                '& .MuiDataGrid-virtualScroller': {
                                    scrollbarWidth: 'thin', // For Firefox
                                    '&::-webkit-scrollbar': {
                                        display: 'block', // Ensure scrollbar is visible
                                        width: '8px',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        backgroundColor: '#888', // Customize thumb color
                                        borderRadius: '4px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        backgroundColor: '#f0f0f0', // Customize track color
                                    },
                                },
                                ".MuiTablePagination-displayedRows, .MuiTablePagination-selectLabel": {
                                    marginTop: "1em",
                                    marginBottom: "1em"
                                },
                                '.column-header-0': {
                                    paddingLeft: '1em', // Adjust the padding value as needed
                                },
                                '.column-cell-0': {
                                    paddingLeft: '1em', // Adjust the padding value as needed
                                },
                                "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
                                    outline: "none !important",
                                }, "&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus": {
                                    outline: "none !important",
                                },
                            }}
                        />
                    </Paper>
            }
        </Box>
    )
}

export default SearchResults;